import React from "react";
import { Avatar, Badge, Chip, InsightBox } from "../../../ForBrands/ui/BrandUI";
import { ChipScroller, ListRule, MetaCols, MobileCard, MobileEmpty, MobilePageHead, StatList, TextLink } from "../../../ForBrands/ui/mobile/MobileUI";
import { fmtDate } from "../../../ForBrands/brandDealUtils";
import { EMPTY_VALUE, athleteDealAction, dealStatusMeta, fmtMoney } from "../athleteUtils";
import iconSearch from "../../../../assets/brand/icon-search.svg";

// Athlete Mode · My Deals on phones (Figma 04 · My Deals, 2000:899). The
// page keeps the data, filters and handlers; this is only the phone tree:
// the head, a 56px search, the filter chips in a side scroller, one card per
// deal, then the compact value analysis with its figures as a stat list.
// A card holds two actions (the next step and "Send message"), so it is not
// one link (D-a16).

// Input · Mobile with an icon (2022:1738). The text is 16px so iOS Safari
// does not zoom on focus (D-g6). The whole box is the label, so a tap
// anywhere in it focuses the field.
const PhoneSearch = ({ value, onChange, placeholder, label }) => (
  <label className="flex h-14 items-center gap-[10px] rounded-[12px] border border-bm-border bg-white px-[18px] focus-within:border-bm-ink focus-within:shadow-[inset_0_0_0_1px_#2c2c2c]">
    <img src={iconSearch} alt="" className="h-4 w-4 shrink-0 opacity-70" />
    <input
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      enterKeyHint="search"
      className="bm-bare min-w-0 flex-1 bg-transparent font-inter text-[16px] leading-[22px] text-bm-ink outline-none placeholder:text-bm-placeholder"
    />
  </label>
);

// Deal card (2022:1754): campaign initials on a square muted avatar, the
// campaign and brand, the status badge as on desktop, the value and
// deadline, then the two actions.
const DealCard = ({ deal, opening, onMessage }) => {
  const meta = dealStatusMeta(deal.status);
  const action = athleteDealAction(deal);
  const busy = opening === deal.id;
  return (
    <MobileCard as="li">
      <div className="flex items-center gap-3">
        <Avatar
          initials={deal.campaignInitials || deal.brand?.initials}
          name={deal.campaignName || deal.brand?.company}
          size={40}
          tone="muted"
          square
          className="!font-semibold !text-[12px]"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-[2px] break-words">
          <h2 className="bm-h-xs text-bm-ink">{deal.campaignName || "Untitled campaign"}</h2>
          {deal.brand?.company && <p className="bm-body-s text-bm-muted">{deal.brand.company}</p>}
        </div>
        <Badge tone={meta.tone} dot={meta.dot} className="shrink-0">
          {meta.label}
        </Badge>
      </div>
      <ListRule />
      <MetaCols
        items={[
          { key: "value", label: "Value", value: fmtMoney(deal.value, { cents: true }) },
          { key: "deadline", label: "Deadline", value: deal.deadline ? fmtDate(deal.deadline) : EMPTY_VALUE },
        ]}
      />
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <TextLink to={action.href}>{action.label}</TextLink>
        <TextLink tone="muted" arrow={false} busy={busy} onClick={() => onMessage(deal)}>
          {busy ? "Opening..." : "Send message"}
        </TextLink>
      </div>
    </MobileCard>
  );
};

const DealsMobile = ({
  summary,
  filters,
  filter,
  onFilter,
  search,
  onSearch,
  rows,
  emptyText,
  opening,
  onMessage,
  insightTitle,
  insightText,
  stats,
}) => (
  <div className="flex flex-col gap-[18px]">
    <MobilePageHead title="Your Deals" sub={summary} />

    <PhoneSearch label="Search deals" value={search} onChange={onSearch} placeholder="Search campaign or brand" />

    <ChipScroller>
      {filters.map((f) => (
        <Chip key={f.value} active={filter === f.value} onClick={() => onFilter(f.value)}>
          {f.label}
        </Chip>
      ))}
    </ChipScroller>

    {rows.length === 0 ? (
      <MobileEmpty>{emptyText}</MobileEmpty>
    ) : (
      // eslint-disable-next-line jsx-a11y/no-redundant-roles -- VoiceOver drops the list role of a list with no bullets
      <ul role="list" className="flex flex-col gap-[18px]">
        {rows.map((d) => (
          <DealCard key={d.id} deal={d} opening={opening} onMessage={onMessage} />
        ))}
      </ul>
    )}

    <InsightBox compact title={insightTitle}>
      {insightText}
    </InsightBox>
    <StatList rows={stats} />
  </div>
);

export default DealsMobile;
